import type {
  BindingDetailsMap,
  BindingTargetDetails,
  BoundObjectRef,
  DataBindingServiceAPI,
  FailedToFindBindingCandidatesError,
  FailedToGetBindingError,
  FailedToListBindingsError,
  FailedToSetBindingError,
  ProvidersServiceAPI,
  Provider,
  AttachStateFilter,
  FailedToUnbindError,
  FailedToListProvidersError,
  FailedToBindError,
} from '@wix/editor-platform-feature-store';
import { CompRef } from '@wix/document-services-types';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../../errors';
import {
  AttachState,
  BindingCandidate,
  BindingProperties,
  ListBindingsOptions,
  ProviderType,
  UnbindAll,
} from '@wix/public-editor-platform-interfaces';

import type { EditorPointer } from '@wix/editor-document-logic-api';

export class ProviderComponentApi {
  constructor(
    private readonly providersAPI: ProvidersServiceAPI | undefined,
    private readonly bindingsAPI: DataBindingServiceAPI | undefined,
  ) {}

  getData(
    editorPointer: EditorPointer<CompRef>,
    providerIdentifier: string,
  ): Record<string, unknown> {
    try {
      if (!this.providersAPI) {
        throw new Error('providers package is not supported');
      }

      const data = this.providersAPI.getData(editorPointer, providerIdentifier);
      if (data && data instanceof Error) {
        throw data;
      }

      return data ?? {};
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error
          ? error.message
          : 'get provider data operation failed',
      );
    }
  }

  setData(
    editorPointer: EditorPointer<CompRef>,
    providerIdentifier: string,
    data: Record<string, unknown>,
  ): void {
    try {
      if (!this.providersAPI) {
        throw new Error('providers package is not supported');
      }
      const result = this.providersAPI.updateData(
        editorPointer,
        providerIdentifier,
        data,
      );
      if (result && result instanceof Error) {
        throw result;
      }
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error
          ? error.message
          : 'set provider data operation failed',
      );
    }
  }

  async bind(
    target: BoundObjectRef,
    bindings: BindingProperties[],
  ): Promise<void | FailedToBindError> {
    try {
      if (!this.bindingsAPI) {
        throw new Error('bindings package is not supported');
      }
      return this.bindingsAPI.bind(target, bindings);
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error ? error.message : 'bind operation failed',
      );
    }
  }

  async setBinding(
    target: BoundObjectRef,
    bindings: BindingDetailsMap,
  ): Promise<void | FailedToSetBindingError> {
    try {
      if (!this.bindingsAPI) {
        throw new Error('bindings package is not supported');
      }
      return this.bindingsAPI.setBinding(target, bindings);
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error ? error.message : 'set binding operation failed',
      );
    }
  }

  async unbind(
    target: BoundObjectRef,
    props: string[] | UnbindAll,
  ): Promise<void | FailedToUnbindError> {
    try {
      if (!this.bindingsAPI) {
        throw new Error('bindings package is not supported');
      }
      return this.bindingsAPI.unbind(target, props);
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error ? error.message : 'unbind operation failed',
      );
    }
  }

  listBindings(
    target: BoundObjectRef,
    options?: ListBindingsOptions,
  ): BindingProperties[] | FailedToListBindingsError {
    try {
      if (!this.bindingsAPI) {
        throw new Error('bindings package is not supported');
      }
      return this.bindingsAPI.listBindings(target, options);
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error
          ? error.message
          : 'list bindings operation failed',
      );
    }
  }

  getBinding(
    target: BoundObjectRef,
    options?: ListBindingsOptions,
  ): BindingDetailsMap | FailedToGetBindingError {
    try {
      if (!this.bindingsAPI) {
        throw new Error('bindings package is not supported');
      }
      return this.bindingsAPI.getBinding(target, options);
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error ? error.message : 'get binding operation failed',
      );
    }
  }

  findBindingCandidates(options: {
    target: BoundObjectRef;
    bindingTargetDetails: BindingTargetDetails;
    filterAttachState?: AttachState[];
    includeCandidatesWithConverters?: boolean;
  }): BindingCandidate[] | FailedToFindBindingCandidatesError {
    try {
      if (!this.bindingsAPI) {
        throw new Error('bindings package is not supported');
      }

      return this.bindingsAPI.findBindingCandidates(options);
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error
          ? error.message
          : 'find binding candidates operation failed',
      );
    }
  }

  list(
    target: BoundObjectRef,
    options?: {
      includeAttachState?: AttachStateFilter;
      includeDependencies?: boolean;
      includeItems?: boolean;
      providerIdentifiers?: string[];
      providerTypes?: ProviderType[];
    },
  ): Provider[] | FailedToListProvidersError {
    try {
      if (!this.providersAPI) {
        throw new Error('providers package is not supported');
      }
      if (options?.includeAttachState?.target?.editorPointer) {
        options.includeAttachState = {
          ...options.includeAttachState,
          target,
        };
      }

      return this.providersAPI.list(options);
    } catch (error) {
      console.error(error);
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ProviderComponentDataApi,
        error instanceof Error
          ? error.message
          : 'list providers operation failed',
      );
    }
  }
}
